import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, RotateCcw, Plus, Trash2 } from "lucide-react";
import { API } from "../../lib/site";
import { DEFAULTS } from "../../lib/defaults";

const SECTIONS = [
  {
    key: "hero", label: "Hero", type: "object",
    fields: [
      { k: "eyebrow", label: "Eyebrow text" },
      { k: "line1", label: "Headline line 1" },
      { k: "line2", label: "Headline line 2" },
      { k: "line3", label: "Headline line 3 (bronze accent)" },
      { k: "sub", label: "Sub-headline", type: "textarea" },
      { k: "image", label: "Hero image URL", type: "image" },
      { k: "badge_label", label: "Floating badge — small label" },
      { k: "badge_title", label: "Floating badge — title" },
    ],
  },
  {
    key: "story", label: "Story", type: "object",
    fields: [
      { k: "headline1", label: "Headline part 1" },
      { k: "headline_accent", label: "Headline accent (bronze italic)" },
      { k: "headline2", label: "Headline part 3" },
      { k: "body", label: "Story paragraph", type: "textarea" },
      { k: "image", label: "Story image URL", type: "image" },
      { k: "badge_value", label: "Badge value (e.g. 10+)" },
      { k: "badge_label", label: "Badge label" },
    ],
  },
  {
    key: "socials", label: "Contact & Socials", type: "object",
    fields: [
      { k: "phone", label: "Phone (display)" },
      { k: "whatsapp", label: "WhatsApp number (e.g. +971502740497)" },
      { k: "email", label: "Email" },
      { k: "hours", label: "Working hours" },
      { k: "facebook", label: "Facebook URL" },
      { k: "instagram", label: "Instagram URL" },
      { k: "google_maps", label: "Google Maps link (share link, or embed URL for a live map)" },
      { k: "areas", label: "Areas served (comma separated)", type: "textarea" },
    ],
  },
  {
    key: "seo", label: "SEO & AEO", type: "object",
    fields: [
      { k: "meta_title", label: "Page title (30–60 characters)" },
      { k: "og_image", label: "Social share image URL", type: "image" },
      { k: "meta_description", label: "Meta description (120–165 characters)", type: "textarea" },
      { k: "meta_keywords", label: "Keywords (comma separated)", type: "textarea" },
    ],
  },
  {
    key: "stats", label: "Stats", type: "list", fixed: 4,
    itemFields: [
      { k: "to", label: "Number", type: "number" },
      { k: "suffix", label: "Suffix (+, %)" },
      { k: "label", label: "Label" },
    ],
  },
  {
    key: "collections", label: "Collections", type: "list", fixed: 4,
    itemFields: [
      { k: "name", label: "Name" },
      { k: "tag", label: "Tag" },
      { k: "img", label: "Image URL", type: "image" },
    ],
  },
  {
    key: "testimonials", label: "Testimonials", type: "list",
    itemFields: [
      { k: "name", label: "Customer name" },
      { k: "location", label: "Location" },
      { k: "text", label: "Review", type: "textarea" },
    ],
  },
  {
    key: "faqs", label: "FAQs", type: "list",
    itemFields: [
      { k: "q", label: "Question" },
      { k: "a", label: "Answer", type: "textarea" },
    ],
  },
];

const inputCls =
  "w-full rounded-xl border border-sand-border bg-sand-bg/50 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-reef/40 focus:border-reef transition";
const labelCls = "block font-mono text-[10px] tracking-[0.15em] uppercase text-slate-500 mb-1.5";

function Field({ field, value, onChange, testid }) {
  return (
    <div>
      <label className={labelCls}>{field.label}</label>
      {field.type === "textarea" ? (
        <textarea data-testid={testid} rows={3} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={`${inputCls} resize-y`} />
      ) : (
        <input
          data-testid={testid}
          type={field.type === "number" ? "number" : "text"}
          step={field.type === "number" ? "any" : undefined}
          value={value ?? ""}
          onChange={(e) => onChange(field.type === "number" ? parseFloat(e.target.value) || 0 : e.target.value)}
          className={inputCls}
        />
      )}
      {field.type === "image" && value ? (
        <img src={value} alt="preview" className="mt-2 h-20 rounded-lg border border-sand-border object-cover" />
      ) : null}
    </div>
  );
}

export default function ContentTab() {
  const [sectionKey, setSectionKey] = useState("hero");
  const [value, setValue] = useState(null);
  const [saving, setSaving] = useState(false);

  const section = SECTIONS.find((s) => s.key === sectionKey);
  const isList = section.type === "list";
  const ready =
    value !== null &&
    (isList ? Array.isArray(value) : typeof value === "object" && !Array.isArray(value));

  const switchSection = (key) => {
    if (key === sectionKey) return;
    setValue(null);
    setSectionKey(key);
  };

  useEffect(() => {
    setValue(null);
    axios
      .get(`${API}/content`)
      .then((res) => setValue(res.data?.[sectionKey] ?? DEFAULTS[sectionKey]))
      .catch(() => setValue(DEFAULTS[sectionKey]));
  }, [sectionKey]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/content/${sectionKey}`, { value }, { withCredentials: true });
      toast.success(`${section.label} updated — live on the site now.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save. Are you still logged in?");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    try {
      const res = await axios.delete(`${API}/admin/content/${sectionKey}`, { withCredentials: true });
      setValue(res.data.value);
      toast.success(`${section.label} reset to default.`);
    } catch {
      toast.error("Reset failed.");
    }
  };

  const setField = (k, v) => setValue((prev) => ({ ...prev, [k]: v }));
  const setItemField = (i, k, v) =>
    setValue((prev) => prev.map((item, idx) => (idx === i ? { ...item, [k]: v } : item)));
  const addItem = () => {
    const blank = Object.fromEntries(section.itemFields.map((f) => [f.k, f.type === "number" ? 0 : ""]));
    setValue((prev) => [...prev, blank]);
  };
  const removeItem = (i) => setValue((prev) => prev.filter((_, idx) => idx !== i));

  return (
    <div data-testid="admin-content-tab">
      <div className="flex flex-wrap gap-2 mb-6">
        {SECTIONS.map((s) => (
          <button
            key={s.key}
            data-testid={`admin-content-section-${s.key}`}
            onClick={() => switchSection(s.key)}
            className={`px-4 py-2 rounded-full font-mono text-[11px] tracking-[0.12em] uppercase transition-colors ${
              sectionKey === s.key ? "bg-reef text-sand-bg" : "border border-sand-border text-slate-600 hover:border-reef/40"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {!ready ? (
        <p className="text-sm text-slate-400 py-10 text-center font-mono animate-pulse">Loading section…</p>
      ) : (
        <div className="bg-white border border-sand-border rounded-2xl p-6 sm:p-8">
          {!isList && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {section.fields.map((f) => (
                <div key={f.k} className={f.type === "textarea" || f.type === "image" ? "sm:col-span-2" : ""}>
                  <Field field={f} value={value[f.k]} onChange={(v) => setField(f.k, v)} testid={`admin-content-field-${sectionKey}-${f.k}`} />
                </div>
              ))}
            </div>
          )}

          {isList && (
            <div className="space-y-4">
              {value.map((item, i) => (
                <div key={i} data-testid={`admin-content-item-${i}`} className="border border-sand-border rounded-xl p-5 relative">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {section.itemFields.map((f) => (
                      <div key={f.k} className={f.type === "textarea" || f.type === "image" ? "sm:col-span-2" : ""}>
                        <Field field={f} value={item[f.k]} onChange={(v) => setItemField(i, f.k, v)} testid={`admin-content-field-${sectionKey}-${i}-${f.k}`} />
                      </div>
                    ))}
                  </div>
                  {!section.fixed && (
                    <button
                      data-testid={`admin-content-remove-${i}`}
                      onClick={() => removeItem(i)}
                      className="absolute top-3 right-3 text-slate-300 hover:text-red-600 transition-colors"
                      aria-label="Remove item"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              {!section.fixed && (
                <button
                  data-testid="admin-content-add-item"
                  onClick={addItem}
                  className="w-full rounded-xl border border-dashed border-reef/40 py-3.5 font-mono text-[11px] tracking-[0.15em] uppercase text-reef hover:bg-reef-light transition-colors flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" /> Add item
                </button>
              )}
            </div>
          )}

          <div className="mt-7 flex flex-wrap gap-3">
            <button
              data-testid="admin-content-save-btn"
              onClick={save}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-3 rounded-full bg-reef text-sand-bg font-mono text-[11px] tracking-[0.15em] uppercase hover:bg-reef-dark transition-colors disabled:opacity-60"
            >
              <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save changes"}
            </button>
            <button
              data-testid="admin-content-reset-btn"
              onClick={reset}
              className="flex items-center gap-2 px-6 py-3 rounded-full border border-sand-border text-slate-600 font-mono text-[11px] tracking-[0.15em] uppercase hover:border-red-200 hover:text-red-600 transition-colors"
            >
              <RotateCcw className="w-4 h-4" /> Reset to default
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
